/**
 * Y-Offset defaults for a brand-new SBS row (hybrid Add Section).
 *
 * `tunnel_offset_direction` / `tunnel_offset` are the pair behind the Data
 * Bar's "Y-Offset" control, and both the model default and legacy's create
 * path (`api.js` sends neither) leave every new tunnel on `centered` / 0.
 *
 * For a back-to-back peer (position > 1) that is right: the chain grows along
 * X inside one row, so sharing the upstream peer's centerline is what you
 * want. For a brand-new ROW it is not. A new row's Y peer is the lead tunnel
 * of the row to its left (`getTunnelZOffset`'s `row - 1` branch), so
 * `centered` gives the new row that peer's centerline exactly — it is drawn
 * inside the row it is supposed to sit beside, and stays there until someone
 * works out a Y-Offset by hand.
 *
 * So a new row starts on `user_defined_left` at the offset that butts it
 * against the row to its left, and the user changes it from there.
 *
 * Ported from `canvas3dGeometry`'s `getTunnelZOffset` rather than reused, for
 * the same reason `hybridXOffset` is a port: that one is a method on the
 * canvas geometry class, works in canvas units (its `coef`), and takes a
 * `CanvasTunnelModel`, not the plain `IAhuBoardTunnel` the insert flow has.
 * Everything here is raw inches.
 */
import { getTunnelPhysicalSpan, type TunnelSpanFields } from './hybridXOffset';

/** Deliberately structural, matching `TunnelSpanFields` — see that comment. */
export interface TunnelYFields {
  id: number;
  row?: number | null;
  level?: number | null;
  position?: number | null;
  width?: number | string | null;
  airflow_direction_id?: number | string | null;
  tunnel_offset_direction?: string | null;
  tunnel_offset?: number | string | null;
}

const num = (v: number | string | null | undefined): number => parseFloat(String(v ?? 0)) || 0;

const addressOf = (t: TunnelYFields) => ({
  row: t.row || 1,
  level: t.level || 1,
  position: t.position || 1,
});

const findAt = <T extends TunnelYFields>(
  tunnels: T[],
  row: number,
  level: number,
  position: number
): T | undefined =>
  tunnels.find((t) => {
    const a = addressOf(t);
    return a.row === row && a.level === level && a.position === position;
  });

/**
 * The Y peer a tunnel's own `tunnel_offset_direction` / `tunnel_offset` are
 * measured against — `getTunnelZOffset`'s peer resolution, unchanged: the
 * upstream back-to-back peer for position > 1, the level below for an OU
 * tunnel, then the row to the left, then the 1:1:1 anchor.
 */
export function findYPeer<T extends TunnelYFields>(tunnel: T, tunnels: T[]): T | undefined {
  const { row, level, position } = addressOf(tunnel);
  let peer: T | undefined;
  if (position > 1) peer = findAt(tunnels, row, level, position - 1);
  else if (level > 1) peer = findAt(tunnels, row, level - 1, 1);
  if (!peer && position <= 1) {
    if (row > 1) peer = findAt(tunnels, row - 1, level, 1);
    if (!peer && !(row <= 1 && level <= 1)) peer = findAt(tunnels, 1, 1, 1);
  }
  return peer;
}

/**
 * A tunnel's Y centerline in inches, with the 1:1:1 anchor at 0 — the inches
 * half of `getTunnelZOffset`. The no-peer fallback there is the non-hybrid
 * "centre within max_width" default, which has no meaning off-canvas; here
 * only 1:1:1 itself can reach it, so it is 0.
 */
export function getTunnelCenterline<T extends TunnelYFields>(tunnel: T, tunnels: T[]): number {
  const { row, level, position } = addressOf(tunnel);
  if (row <= 1 && level <= 1 && position <= 1) return 0;
  const peer = findYPeer(tunnel, tunnels);
  if (!peer) return 0;
  const width = num(tunnel.width);
  const peerWidth = num(peer.width);
  const peerCenter = getTunnelCenterline(peer, tunnels);
  // `unit` — the peer's airflow direction mirrors what "left" and "right"
  // mean, exactly as the canvas switch does.
  const unit = -2 * (Number(peer.airflow_direction_id) || 1) + 3;
  const offset = num(tunnel.tunnel_offset);
  switch (tunnel.tunnel_offset_direction || 'centered') {
    case 'left_aligned':
      return peerCenter + (unit * (width - peerWidth)) / 2;
    case 'right_aligned':
      return peerCenter + (unit * (peerWidth - width)) / 2;
    // Measured from the peer's CENTERLINE, not from the aligned face —
    // `tunnel_offset` is a centre-to-centre distance. See the same two cases
    // in `getTunnelZOffset` for the full why.
    case 'user_defined_left':
      return peerCenter - unit * offset;
    case 'user_defined_right':
      return peerCenter + unit * offset;
    case 'centered':
    default:
      return peerCenter;
  }
}

/** Distance from `x` to a span, 0 when `x` falls inside it. */
const distanceToSpan = (x: number, span: { left: number; right: number }): number =>
  x < span.left ? span.left - x : x > span.right ? x - span.right : 0;

export interface RowYOffsetDefault {
  tunnel_offset_direction: 'user_defined_left';
  tunnel_offset: number;
}

/**
 * The Y-Offset a tunnel starting a brand-new row defaults to: sat immediately
 * beside the nearest section of the row to its left, measuring "nearest" from
 * the new section's own upstream edge (`upstreamEdgeX`, which is what
 * `x_offset` holds — see `hybridXOffset`).
 *
 * Nearest matters because a row is a chain of tunnels along X, each with its
 * own width and its own Y-Offset against the one upstream of it, so "beside
 * the row to the left" has as many answers as that row has tunnels. The
 * tunnel whose span the new section actually starts against is the one it
 * should line up with.
 *
 * `tunnel_offset` is then that placement expressed in the stored convention,
 * which is always measured against the LEAD tunnel of the row to the left
 * (the Y peer), not against the neighbour we picked:
 *
 *     offset = (width + neighbourWidth) / 2
 *              + unit * (peerCenter - neighbourCenter)
 *
 * `tunnel_offset` being a centre-to-centre distance, the first term is the
 * Y-Offset help text's own `nearest section width / 2 + new section width / 2`
 * verbatim; the second re-bases it from the neighbour we picked onto the lead.
 * With a single-tunnel row to the left it collapses to that first term, and to
 * `width` again when the new row inherits the lead's width.
 *
 * Returns null when there is nothing to sit beside (no row to the left, or no
 * lead tunnel in it), leaving the model default of `centered` in place.
 */
export function defaultRowYOffset<T extends TunnelYFields & TunnelSpanFields>(args: {
  /** Address of the tunnel about to be created. */
  row: number;
  level: number;
  /** Its width — inherited from the peer it is created alongside. */
  width: number;
  /** Its upstream edge's absolute X, i.e. the `x_offset` being sent. */
  upstreamEdgeX: number;
  tunnels: T[];
}): RowYOffsetDefault | null {
  const { row, level, width, upstreamEdgeX, tunnels } = args;
  if (row <= 1) return null;
  // The stored offset is only measured against the row to the left when
  // `getTunnelZOffset` actually resolves that peer — an OU tunnel aligns
  // against the support on the level below it instead, and `centered` is
  // right there.
  if (level > 1 && findAt(tunnels, row, level - 1, 1)) return null;
  const peer = findAt(tunnels, row - 1, level, 1);
  if (!peer) return null;
  const neighbour = tunnels
    .filter((t) => {
      const a = addressOf(t);
      return a.row === row - 1 && a.level === level;
    })
    .map((t) => ({ tunnel: t, span: getTunnelPhysicalSpan(t) }))
    .sort(
      (a, b) =>
        distanceToSpan(upstreamEdgeX, a.span) - distanceToSpan(upstreamEdgeX, b.span) ||
        a.span.left - b.span.left ||
        a.tunnel.id - b.tunnel.id
    )[0]?.tunnel;
  if (!neighbour) return null;

  const unit = -2 * (Number(peer.airflow_direction_id) || 1) + 3;
  const peerCenter = getTunnelCenterline(peer, tunnels);
  const neighbourCenter = getTunnelCenterline(neighbour, tunnels);
  const offset =
    (width + num(neighbour.width)) / 2 + unit * (peerCenter - neighbourCenter);
  return {
    tunnel_offset_direction: 'user_defined_left',
    // Negative would mean butting up on the left of a neighbour that is
    // itself further right of the lead tunnel than the new row is wide —
    // unreachable while a new row inherits the lead's width and the row to
    // the left is `centered` throughout, and the stored field is unsigned
    // (the direction carries the sign), so fall back to flush with the lead.
    tunnel_offset: Math.max(0, Math.round(offset * 100) / 100),
  };
}
